import { Group } from 'src/design/atoms/Group';
import { MRT_ColumnDef } from 'mantine-react-table';
import React, { useMemo } from 'react';
import { ContactDto } from 'src/api/FiretrolApiClient';
import { useOrganization } from 'src/hooks/useOrganization';
import { EditActionIcon } from 'src/design/atoms/EditActionIcon';
import { CreateButton } from 'src/design/atoms/CreateButton';
import { Header } from 'src/design/organisms/Header';
import {
  useTable,
  useTableState,
} from 'src/design/organisms/DataTable/useTable';
import { useTableExports } from 'src/design/organisms/useTableExports';
import { CreateContactDialog } from './components/CreateContactDialog';

const getColumns = (): MRT_ColumnDef<ContactDto>[] => {
  return [
    {
      accessorKey: 'firstName',
      header: 'First',
    },
    {
      accessorKey: 'lastName',
      header: 'Last',
    },
    {
      accessorKey: 'phone',
      header: 'Phone',
    },
    {
      accessorKey: 'email',
      header: 'Email',
    },
    {
      accessorKey: 'customerName',
      header: 'Company',
      Cell: ({ cell }) => cell.getValue<string>() || '',
    },
  ];
};

export const Contacts: React.FC = () => {
  const [createContactOpen, setCreateContactOpen] = React.useState<{
    isOpen: boolean;
    contact: ContactDto | null;
  }>({
    isOpen: false,
    contact: null,
  });

  const { loading, contacts } = useOrganization();
  const setContactOpen = (isOpen: boolean, contact: ContactDto | null) => {
    setCreateContactOpen({ isOpen, contact });
  };

  const columns = useMemo(() => getColumns(), []);

  const tableState = useTableState({
    identifier: 'contacts',
    columns: columns,
    defaultSorting: [{ id: 'lastName', desc: false }],
  });

  const tableExports = useTableExports<ContactDto>('contacts', (data) => {
    return data.map((item) => ({
      ['First']: item.firstName,
      ['Last']: item.lastName,
      ['Phone']: item.phone || '',
      ['Email']: item.email || '',
      ['Company']: item.customerName || '',
    }));
  });

  const { table } = useTable({
    ...tableState,
    tableOptions: {
      enableRowActions: true,
      displayColumnDefOptions: { 'mrt-row-actions': { size: 100 } },
      renderRowActions: ({ row }) => {
        const contact = row.original;
        return (
          <Group>
            <EditActionIcon onClick={() => setContactOpen(true, contact)} />
          </Group>
        );
      },
      enableClickToCopy: false,
      columns: columns,
      data: contacts,
      manualPagination: false,
      manualSorting: false,
      manualFiltering: false,
      renderTopToolbarCustomActions: () => (
        <Group>
          <CreateButton onClick={() => setContactOpen(true, null)}>
            Create Contact
          </CreateButton>
        </Group>
      ),
    },
    getRowId: (row) => String(row.id),
    totalCount: contacts.length,
    isLoading: loading,
    isFetching: false,
    isError: false,
    ...tableExports,
  });

  return (
    <>
      <Header
        breadcrumbProps={{
          items: [{ text: 'Settings' }, { text: 'Contacts' }],
        }}
        title="Contacts"
        helpText="View and manage your organization's contacts"
      />
      <CreateContactDialog
        open={createContactOpen.isOpen}
        onClose={() => setContactOpen(false, null)}
        contact={createContactOpen.contact}
      />
      {table}
    </>
  );
};
